import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { useToast } from '../ui/ToastProvider'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import MenuGuideButton from '../components/MenuGuideButton'

const STATUS_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'UNUSED', label: '미사용' },
    { value: 'USED', label: '사용완료' },
    { value: 'REVOKED', label: '폐기(대체 발급 등)' },
    { value: 'EXPIRED', label: '만료' },
]

const STATUS_BADGE = {
    UNUSED: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    USED: 'bg-sky-50 text-sky-800 border-sky-200',
    REVOKED: 'bg-amber-50 text-amber-900 border-amber-200',
    EXPIRED: 'bg-slate-100 text-slate-700 border-slate-200',
}

function unwrapData(res) {
    return res?.data?.data ?? res?.data
}

export default function OpsKakaoTalkManagementPage() {
    const { hasAnyRole } = useAuth()
    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false
    const { show } = useToast()

    const [tab, setTab] = useState('history')

    const [programCode, setProgramCode] = useState('KAKAO_SENDER')
    const [orgIdFilter, setOrgIdFilter] = useState('')
    const [status, setStatus] = useState('')
    const [page, setPage] = useState(0)
    const [size] = useState(30)
    const [loading, setLoading] = useState(false)
    const [listPage, setListPage] = useState(null)

    const [orgs, setOrgs] = useState([])
    const [orgQuery, setOrgQuery] = useState('')
    const [issueOrgId, setIssueOrgId] = useState('')
    const [issueProgramCode, setIssueProgramCode] = useState('KAKAO_SENDER')
    const [issuing, setIssuing] = useState(false)
    const [lastIssued, setLastIssued] = useState(null)

    const loadList = useCallback(async () => {
        setLoading(true)
        try {
            const params = {
                page,
                size,
            }
            if (programCode.trim()) params.programCode = programCode.trim()
            const oid = orgIdFilter.trim()
            if (oid && /^\d+$/.test(oid)) params.orgId = Number(oid)
            if (status) params.status = status

            const res = await api.get('/admin/external/install-codes', { params })
            const data = unwrapData(res)
            setListPage(data)
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            show('목록을 불러오지 못했습니다: ' + msg, 'error')
            setListPage(null)
        } finally {
            setLoading(false)
        }
    }, [page, size, programCode, orgIdFilter, status, show])

    useEffect(() => {
        if (!isOperator || tab !== 'history') return
        loadList()
    }, [isOperator, tab, loadList])

    const loadOrgs = useCallback(async () => {
        try {
            const res = await api.get('/orgs/options', { params: { scope: 'all' } })
            const rows = unwrapData(res)
            setOrgs(Array.isArray(rows) ? rows : [])
        } catch (e) {
            console.error(e)
            show('조직 목록을 불러오지 못했습니다.', 'error')
        }
    }, [show])

    useEffect(() => {
        if (!isOperator || tab !== 'issue') return
        loadOrgs()
    }, [isOperator, tab, loadOrgs])

    const filteredOrgs = useMemo(() => {
        const q = orgQuery.trim().toLowerCase()
        const base = Array.isArray(orgs) ? orgs : []
        if (!q) return base.slice(0, 80)
        return base.filter((o) => String(o.name || '').toLowerCase().includes(q)).slice(0, 120)
    }, [orgs, orgQuery])

    const onIssue = async () => {
        const oid = String(issueOrgId || '').trim()
        if (!oid || !/^\d+$/.test(oid)) {
            show('조직을 선택하거나 조직 ID를 입력해 주세요.', 'error')
            return
        }
        setIssuing(true)
        setLastIssued(null)
        try {
            const res = await api.post('/admin/external/install-codes/issue', {
                programCode: issueProgramCode.trim() || 'KAKAO_SENDER',
                orgId: Number(oid),
            })
            const data = unwrapData(res)
            setLastIssued(data)
            show('설치코드가 발급되었습니다. 평문은 이 화면에서만 확인할 수 있습니다.', 'success')
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            show('발급 실패: ' + msg, 'error')
        } finally {
            setIssuing(false)
        }
    }

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text)
            show('복사했습니다', 'success')
        } catch {
            show('복사에 실패했습니다', 'error')
        }
    }

    if (!isOperator) {
        return <div className="p-4">운영자 권한이 필요합니다.</div>
    }

    const items = listPage?.content || []
    const totalPages = listPage?.totalPages ?? 0

    return (
        <section className="p-4 max-w-[1200px]">
            <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900">카카오톡 관리</h1>
                <MenuGuideButton slug="ops-management" title="운영관리 가이드" />
            </div>

            <div className="mt-6 flex gap-1 border-b border-slate-200">
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
                        tab === 'history'
                            ? 'bg-white border-slate-200 text-slate-900 -mb-px'
                            : 'bg-slate-50 border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => setTab('history')}
                >
                    설치코드 이력
                </button>
                <button
                    type="button"
                    className={`px-4 py-2 text-sm font-medium rounded-t-md border border-b-0 ${
                        tab === 'issue'
                            ? 'bg-white border-slate-200 text-slate-900 -mb-px'
                            : 'bg-slate-50 border-transparent text-slate-600 hover:text-slate-900'
                    }`}
                    onClick={() => setTab('issue')}
                >
                    설치코드 발급
                </button>
            </div>

            <div className="rounded-b-lg border border-t-0 border-slate-200 bg-white p-4">
                {tab === 'history' && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-3 items-end">
                            <label className="flex flex-col text-xs text-slate-600">
                                프로그램 코드
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-44"
                                    value={programCode}
                                    onChange={(e) => { setProgramCode(e.target.value); setPage(0) }}
                                    placeholder="KAKAO_SENDER"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                조직 ID
                                <input
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-28"
                                    value={orgIdFilter}
                                    onChange={(e) => { setOrgIdFilter(e.target.value); setPage(0) }}
                                    placeholder="선택"
                                />
                            </label>
                            <label className="flex flex-col text-xs text-slate-600">
                                상태
                                <select
                                    className="mt-1 rounded border px-2 py-1.5 text-sm w-44"
                                    value={status}
                                    onChange={(e) => { setStatus(e.target.value); setPage(0) }}
                                >
                                    {STATUS_OPTIONS.map((o) => (
                                        <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <button
                                type="button"
                                className="rounded bg-slate-800 text-white text-sm px-3 py-2 hover:bg-slate-700"
                                onClick={() => loadList()}
                                disabled={loading}
                            >
                                조회
                            </button>
                        </div>

                        <div className="overflow-x-auto border rounded-md">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-left text-xs text-slate-600">
                                    <tr>
                                        <th className="px-2 py-2 whitespace-nowrap">ID</th>
                                        <th className="px-2 py-2 whitespace-nowrap">프로그램</th>
                                        <th className="px-2 py-2 whitespace-nowrap">조직</th>
                                        <th className="px-2 py-2 whitespace-nowrap">발급자</th>
                                        <th className="px-2 py-2 whitespace-nowrap">상태</th>
                                        <th className="px-2 py-2 whitespace-nowrap">코드 지문</th>
                                        <th className="px-2 py-2 whitespace-nowrap">등록 클라이언트</th>
                                        <th className="px-2 py-2 whitespace-nowrap">기기명</th>
                                        <th className="px-2 py-2 whitespace-nowrap">자격증명</th>
                                        <th className="px-2 py-2 whitespace-nowrap">생성</th>
                                        <th className="px-2 py-2 whitespace-nowrap">만료</th>
                                        <th className="px-2 py-2 whitespace-nowrap">사용 시각</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr>
                                            <td colSpan={12} className="px-3 py-6 text-center text-slate-500">불러오는 중…</td>
                                        </tr>
                                    )}
                                    {!loading && items.length === 0 && (
                                        <tr>
                                            <td colSpan={12} className="px-3 py-6 text-center text-slate-500">데이터가 없습니다.</td>
                                        </tr>
                                    )}
                                    {!loading && items.map((row) => (
                                        <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/80">
                                            <td className="px-2 py-2 whitespace-nowrap font-mono text-xs">{row.id}</td>
                                            <td className="px-2 py-2 whitespace-nowrap">{row.programCode}</td>
                                            <td className="px-2 py-2">
                                                <div className="max-w-[180px] truncate" title={row.orgName}>{row.orgName || '-'}</div>
                                                <div className="text-xs text-slate-500">{row.orgId != null ? `#${row.orgId}` : ''}</div>
                                            </td>
                                            <td className="px-2 py-2">
                                                <div className="max-w-[160px] truncate" title={row.issuedByEmail}>{row.issuedByEmail || '-'}</div>
                                                {row.issuedByName && (
                                                    <div className="text-xs text-slate-500 truncate max-w-[160px]">{row.issuedByName}</div>
                                                )}
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-0.5 rounded border text-xs ${STATUS_BADGE[row.status] || 'bg-slate-50 border-slate-200'}`}>
                                                    {STATUS_OPTIONS.find((s) => s.value === row.status)?.label || row.status}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2 font-mono text-xs whitespace-nowrap">{row.codeFingerprint}</td>
                                            <td className="px-2 py-2 font-mono text-xs max-w-[140px] truncate" title={row.usedClientId}>{row.usedClientId || '-'}</td>
                                            <td className="px-2 py-2 max-w-[120px] truncate" title={row.usedDeviceName}>{row.usedDeviceName || '-'}</td>
                                            <td className="px-2 py-2 text-xs whitespace-nowrap">
                                                {row.credentialId != null
                                                    ? (row.credentialActive ? '활성' : '비활성')
                                                    : '-'}
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDateTimeMinute(row.createdAt)}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDateTimeMinute(row.expiresAt)}</td>
                                            <td className="px-2 py-2 whitespace-nowrap text-xs">{formatDateTimeMinute(row.usedAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center gap-2 text-sm">
                                <button
                                    type="button"
                                    className="rounded border px-2 py-1 disabled:opacity-40"
                                    disabled={page <= 0}
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                >
                                    이전
                                </button>
                                <span className="text-slate-600">
                                    {page + 1} / {totalPages} (총 {listPage?.totalElements ?? 0}건)
                                </span>
                                <button
                                    type="button"
                                    className="rounded border px-2 py-1 disabled:opacity-40"
                                    disabled={page >= totalPages - 1}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    다음
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {tab === 'issue' && (
                    <div className="space-y-4 max-w-lg">
                        <p className="text-sm text-slate-600">
                            동일 프로그램·동일 조직에 이미 유효한 미사용 코드가 있으면 새로 발급 시 기존 코드는 자동으로 폐기됩니다.
                            (일반 사용자 화면에서의 발급과 같은 규칙입니다.)
                        </p>
                        <label className="flex flex-col text-xs text-slate-600">
                            프로그램 코드
                            <input
                                className="mt-1 rounded border px-2 py-1.5 text-sm"
                                value={issueProgramCode}
                                onChange={(e) => setIssueProgramCode(e.target.value)}
                            />
                        </label>
                        <label className="flex flex-col text-xs text-slate-600">
                            조직 검색
                            <input
                                className="mt-1 rounded border px-2 py-1.5 text-sm"
                                value={orgQuery}
                                onChange={(e) => setOrgQuery(e.target.value)}
                                placeholder="조직명 일부"
                            />
                        </label>
                        <label className="flex flex-col text-xs text-slate-600">
                            대상 조직
                            <select
                                className="mt-1 rounded border px-2 py-1.5 text-sm"
                                value={issueOrgId}
                                onChange={(e) => setIssueOrgId(e.target.value)}
                            >
                                <option value="">선택…</option>
                                {filteredOrgs.map((o) => (
                                    <option key={o.id} value={o.id}>
                                        [{o.id}] {o.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button
                            type="button"
                            className="rounded bg-brand-sky text-white text-sm px-4 py-2 hover:opacity-90 disabled:opacity-50"
                            onClick={onIssue}
                            disabled={issuing}
                        >
                            {issuing ? '발급 중…' : '설치코드 발급'}
                        </button>

                        {lastIssued && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm space-y-2">
                                <div className="font-semibold text-amber-950">발급 완료 — 평문은 재조회할 수 없습니다</div>
                                <div>조직: {lastIssued.orgName} (#{lastIssued.orgId})</div>
                                <div>만료: {formatDateTimeMinute(lastIssued.expiresAt)} (약 30분)</div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-mono font-semibold tracking-wide">{lastIssued.installCode}</span>
                                    <button
                                        type="button"
                                        className="text-xs underline text-amber-900"
                                        onClick={() => copyText(lastIssued.installCode)}
                                    >
                                        복사
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    )
}
